import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postRetryPayout, useAdminPayoutsCache } from '../../../api/admin-payouts';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the retry orchestration for the payout detail drawer
 * (AUT-128): fires the destructive "cancel + replace" POST, a success /
 * error toast, and invalidates the `['admin','payouts']` cache so the list
 * + drawer refetch. Extracted from the drawer so the body stays under the
 * line cap. The action is role + permission (`process_payouts`) + audit
 * gated server-side (AUT-138 removed per-action TOTP step-up).
 *
 * 🟠 #4 ERROR MAPPING. The backend is the SOLE retry authority — the list
 * `retryEligible` flag can go stale between fetch and click. Retry
 * rejections arrive as HTTP 422 with a `code` discriminator; the shared
 * interceptor only auto-toasts 403 + 409, so a 422 falls through to HERE.
 * We read `err.response?.data?.code` and map each of the four codes to a
 * specific message; anything else → a generic message. On ANY rejection
 * we `invalidate` so the row's eligibility re-syncs.
 *
 * The retry response envelope (`{dto, previousStatus}`) is the LIST shape
 * (architect 🟠 #7) — NOT used to patch the drawer; `invalidate()`
 * refetches the admin projections instead.
 */

/** The four retry-rejection codes the backend emits (422 + the 409 stale). */
const RETRY_ERROR_CODES = new Set([
  'payout_rail_retry_unsupported',
  'payout_retry_blocked_by_kyc',
  'payout_retry_blocked_by_payment',
  'payout_status_stale',
]);

/** Map a server error `code` to its specific i18n key, else the generic one. */
const retryErrorMessageKey = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const code = (err.response?.data as { code?: string } | undefined)?.code;
    if (code && RETRY_ERROR_CODES.has(code)) return `payouts.retry.error.${code}`;
  }
  return 'payouts.retry.error.generic';
};

export interface UseRetryPayoutResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a retry; resolves true on success, false on error. */
  run: (id: string) => Promise<boolean>;
}

export const useRetryPayout = (): UseRetryPayoutResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { invalidate } = useAdminPayoutsCache();

  const run = async (id: string): Promise<boolean> => {
    setBusy(true);
    try {
      await postRetryPayout(id);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('payouts.retry.successToast'),
      });
      return true;
    } catch (err: unknown) {
      // The eligibility flag may have gone stale → refresh the row state.
      await invalidate();
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t(retryErrorMessageKey(err)),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
